import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSEO } from '../contexts/SEOContext';

const DEFAULT_OG = ''; // falls back to logo (handled below)

/**
 * SEO - sets title, meta, canonical, OG, Twitter, JSON-LD per page.
 *
 * Props:
 *  - title: string (page specific). Suffix is appended automatically unless noSuffix.
 *  - description: string
 *  - keywords: string
 *  - image: absolute image URL (OG + Twitter)
 *  - noindex: boolean - adds <meta name="robots" content="noindex, nofollow">
 *  - canonicalPath: string - override location.pathname
 *  - type: 'website' | 'article' | 'product' | 'event' (default website)
 *  - noSuffix: boolean
 *  - jsonLd: object or array of objects → <script type="application/ld+json">
 */
export default function SEO({
  title,
  description,
  keywords,
  image,
  noindex = false,
  canonicalPath,
  type = 'website',
  noSuffix = false,
  jsonLd,
}) {
  const { seo } = useSEO();
  const location = useLocation();

  const site = seo || {};
  const baseUrl = useMemo(() => {
    if (site.canonical_base_url) return site.canonical_base_url.replace(/\/+$/, '');
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
  }, [site.canonical_base_url]);

  const path = canonicalPath || location.pathname;
  const url = `${baseUrl}${path}`;

  // Preview/non-canonical hostname → global noindex to prevent duplicate content issues.
  const previewNoindex = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const canonicalHost = (site.canonical_base_url || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
    if (!canonicalHost) return false;
    const currentHost = window.location.hostname.toLowerCase();
    // Allow apex and www as same canonical
    const normalizedCanonical = canonicalHost.replace(/^www\./, '');
    const normalizedCurrent = currentHost.replace(/^www\./, '');
    return normalizedCanonical !== normalizedCurrent;
  }, [site.canonical_base_url]);

  const builtTitle = useMemo(() => {
    const t = title || site.default_title || 'MiniOto';
    if (noSuffix) return t;
    const suf = site.title_suffix || ' · MiniOto';
    return t.endsWith(suf.trim()) || t.includes('MiniOto') ? t : `${t}${suf}`;
  }, [title, noSuffix, site.default_title, site.title_suffix]);

  const builtDesc = description || site.default_description || '';
  const builtKeywords = keywords || site.default_keywords || '';
  const ogImage = image || site.og_image_url || `${baseUrl}/og-image.jpg`;
  const siteName = site.site_name || 'MiniOto';
  const twitter = site.twitter_handle || '';

  // Global noindex rules
  const globalNoindex = Array.isArray(site.noindex_paths)
    ? site.noindex_paths.some(p => p && path.startsWith(p))
    : false;
  const robots = noindex || globalNoindex || previewNoindex ? 'noindex, nofollow' : 'index, follow';

  const jsonLdArr = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="tr" />
      <title>{builtTitle}</title>
      {builtDesc && <meta name="description" content={builtDesc} />}
      {builtKeywords && <meta name="keywords" content={builtKeywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      {site.favicon_url && <link rel="icon" href={site.favicon_url} />}
      {site.apple_touch_icon_url && <link rel="apple-touch-icon" href={site.apple_touch_icon_url} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={builtTitle} />
      {builtDesc && <meta property="og:description" content={builtDesc} />}
      <meta property="og:url" content={url} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={builtTitle} />
      {builtDesc && <meta name="twitter:description" content={builtDesc} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {twitter && <meta name="twitter:site" content={twitter} />}

      {site.google_site_verification && (
        <meta name="google-site-verification" content={site.google_site_verification} />
      )}
      {jsonLdArr.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

export function GlobalSchema() {
  const { seo } = useSEO();
  const site = seo || {};
  const baseUrl = site.canonical_base_url || (typeof window !== 'undefined' ? window.location.origin : '');
  const orgUrl = site.organization_url || baseUrl;
  const org = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.organization_name || 'MiniOto',
    url: orgUrl,
    ...(site.og_image_url ? { logo: site.og_image_url } : {}),
  };
  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.site_name || 'MiniOto',
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/forum?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(org)}</script>
      <script type="application/ld+json">{JSON.stringify(website)}</script>
    </Helmet>
  );
}
